import { doc, getDoc, setDoc, increment, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Limite quotidienne de bande passante en octets (950 Mo)
const DAILY_BANDWIDTH_LIMIT = 950 * 1024 * 1024;

// Document Firestore pour stocker les statistiques de bande passante
const BANDWIDTH_DOC_PATH = 'system/bandwidth';

/**
 * Interface pour les données de bande passante
 */
interface BandwidthData {
  date: Timestamp;
  totalBytes: number;
  lastReset: Timestamp;
}

/**
 * Réinitialise le compteur de bande passante si nécessaire (nouveau jour)
 */
const resetBandwidthIfNeeded = async (): Promise<void> => {
  const docRef = doc(db, BANDWIDTH_DOC_PATH);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    const data = docSnap.data() as BandwidthData;
    const lastResetDate = data.lastReset.toDate();
    const today = new Date();
    
    // Réinitialiser si le dernier reset n'est pas aujourd'hui
    if (lastResetDate.getDate() !== today.getDate() || 
        lastResetDate.getMonth() !== today.getMonth() || 
        lastResetDate.getFullYear() !== today.getFullYear()) {
      await setDoc(docRef, {
        date: Timestamp.now(),
        totalBytes: 0,
        lastReset: Timestamp.now()
      });
      console.log('Compteur de bande passante réinitialisé pour la nouvelle journée');
    }
  } else {
    // Créer le document s'il n'existe pas
    await setDoc(docRef, {
      date: Timestamp.now(),
      totalBytes: 0,
      lastReset: Timestamp.now()
    });
    console.log('Document de suivi de bande passante créé');
  }
};

/**
 * Enregistre la consommation de bande passante
 * @param bytes Nombre d'octets téléchargés
 */
export const trackBandwidthUsage = async (bytes: number): Promise<void> => {
  try {
    // Réinitialiser le compteur si nécessaire
    await resetBandwidthIfNeeded();
    
    // Mettre à jour le compteur
    const docRef = doc(db, BANDWIDTH_DOC_PATH);
    await setDoc(docRef, {
      date: Timestamp.now(),
      totalBytes: increment(bytes)
    }, { merge: true });
    
    console.log(`Bande passante enregistrée: ${bytes} octets`);
  } catch (error) {
    console.error('Erreur lors du suivi de la bande passante:', error);
  }
};

/**
 * Vérifie si la limite de bande passante quotidienne est atteinte
 * @returns true si la limite est atteinte, false sinon
 */
export const isBandwidthLimitReached = async (): Promise<boolean> => {
  try {
    // Réinitialiser le compteur si nécessaire
    await resetBandwidthIfNeeded();
    
    // Vérifier la consommation actuelle
    const docRef = doc(db, BANDWIDTH_DOC_PATH);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as BandwidthData;
      const currentUsage = data.totalBytes || 0;
      
      // Calculer le pourcentage d'utilisation pour les logs
      const usagePercent = (currentUsage / DAILY_BANDWIDTH_LIMIT) * 100;
      console.log(`Utilisation de la bande passante: ${(currentUsage / (1024 * 1024)).toFixed(2)} Mo / ${DAILY_BANDWIDTH_LIMIT / (1024 * 1024)} Mo (${usagePercent.toFixed(2)}%)`);
      
      return currentUsage >= DAILY_BANDWIDTH_LIMIT;
    }
    
    return false;
  } catch (error) {
    console.error('Erreur lors de la vérification de la limite de bande passante:', error);
    // En cas d'erreur, on autorise l'affichage pour éviter de bloquer l'application
    return false;
  }
};

/**
 * Obtient le pourcentage d'utilisation de la bande passante
 * @returns Pourcentage d'utilisation (0-100)
 */
export const getBandwidthUsagePercent = async (): Promise<number> => {
  try {
    // Réinitialiser le compteur si nécessaire
    await resetBandwidthIfNeeded();
    
    // Vérifier la consommation actuelle
    const docRef = doc(db, BANDWIDTH_DOC_PATH);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as BandwidthData;
      const currentUsage = data.totalBytes || 0;
      return (currentUsage / DAILY_BANDWIDTH_LIMIT) * 100;
    }
    
    return 0;
  } catch (error) {
    console.error('Erreur lors de la récupération du pourcentage d\'utilisation:', error);
    return 0;
  }
};
